import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Badge,
} from "reactstrap";

import AgentService from "../../services/agent";
import LocalstorageService from "../../helpers/localstorage-service";
import { CompanyId } from "../../constants";
import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";

function CasponetConfig({ formData }) {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();

  const [casponetUser, setCasponetUser] = useState("");
  const [casponetPassword, setCasponetPassword] = useState("");
  const [tokenExpires, setTokenExpires] = useState(null);
  const [tokenIssued, setTokenIssued] = useState(null);
  const [hasConfig, setHasConfig] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [tokenSuccess, setTokenSuccess] = useState(false);

  const agentId = parseInt(params.agentId);
  const companyId = parseInt(LocalstorageService.getItem(CompanyId));

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const res = await AgentService.getCasponetConfig(agentId);
      if (res.data?.status === 200 && res.data.data) {
        const config = res.data.data;
        setCasponetUser(config.casponetUser || "");
        setCasponetPassword("");
        setTokenExpires(config.expires || null);
        setTokenIssued(config.issued || null);
        setHasConfig(true);
      }
    } catch (error) {
      // No config yet — that's ok
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "---";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const isTokenExpired = () => {
    if (!tokenExpires) return true;
    return new Date(tokenExpires) < new Date();
  };

  const handleSave = async () => {
    if (!casponetUser) {
      toast.error(t("AGENT.CASPONET_USER_REQUIRED") || "נא להזין שם משתמש כספונט");
      return;
    }
    if (!casponetPassword && !hasConfig) {
      toast.error(t("AGENT.CASPONET_PASSWORD_REQUIRED") || "נא להזין סיסמה");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        companyId,
        agentId,
        casponetUser,
        casponetPassword,
      };
      const res = await AgentService.saveCasponetConfig(payload);
      if (res.data?.status === 200) {
        setHasConfig(true);
        setCasponetPassword("");
        if (res.data.data?.tokenCreated) {
          setTokenExpires(res.data.data.expires || null);
          setTokenIssued(res.data.data.issued || null);
          toast.success("סיסמה נשמרה וטוקן נוצר בהצלחה");
        } else if (res.data.data?.tokenCreated === false) {
          toast.warning(res.data.message || "הגדרות נשמרו, אך יצירת טוקן נכשלה — נסה ידנית");
          await loadConfig();
        } else {
          toast.success("הגדרות כספונט נשמרו בהצלחה");
          await loadConfig();
        }
      } else {
        toast.error(res.data?.message || "שגיאה בשמירה");
      }
    } catch (error) {
      toast.error(t("AGENT.CASPONET_SAVE_ERROR") || "שגיאה בשמירת הגדרות כספונט");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTokenAction = async () => {
    setIsCreatingToken(true);
    setTokenSuccess(false);
    try {
      const res = await AgentService.createOrRefreshToken({ agentId });
      if (res.data?.status === 200) {
        setTokenSuccess(true);
        toast.success(t("AGENT.TOKEN_SUCCESS") || "טוקן נוצר/רוענן בהצלחה");
        if (res.data.data) {
          setTokenExpires(res.data.data.expires || null);
          setTokenIssued(res.data.data.issued || null);
        }
        // Auto-hide success after 5 seconds
        setTimeout(() => setTokenSuccess(false), 5000);
      } else {
        toast.error(res.data?.message || "שגיאה ביצירת טוקן");
      }
    } catch (error) {
      toast.error(t("AGENT.TOKEN_ERROR") || "שגיאה ביצירת/רענון טוקן מול כספונט");
    } finally {
      setIsCreatingToken(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Credentials */}
      <Card>
        <CardBody>
          <h5 className="mb-3">
            <i className="ri-key-2-line me-2"></i>
            {t("AGENT.CASPONET_CREDENTIALS") || "פרטי התחברות כספונט"}
          </h5>
          <Row>
            <Col md={4}>
              <Label>{t("AGENT.CASPONET_USER") || "שם משתמש כספונט"}</Label>
              <Input
                type="text"
                value={casponetUser}
                onChange={(e) => setCasponetUser(e.target.value)}
                placeholder={t("AGENT.CASPONET_USER_PLACEHOLDER") || "הזן שם משתמש"}
              />
            </Col>
            <Col md={4}>
              <Label>{t("AGENT.CASPONET_PASSWORD") || "סיסמה"}</Label>
              <Input
                type="password"
                value={casponetPassword}
                onChange={(e) => setCasponetPassword(e.target.value)}
                placeholder={hasConfig ? "••••••••" : (t("AGENT.CASPONET_PASSWORD_PLACEHOLDER") || "הזן סיסמה")}
              />
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
                className="w-100"
              >
                {isSaving ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i className="ri-save-line me-1"></i>
                    {t("SAVE_TITLE") || "שמור"}
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Token Management */}
      <Card className="mt-3">
          <CardBody>
            <h5 className="mb-3">
              <i className="ri-shield-keyhole-line me-2"></i>
              {t("AGENT.TOKEN_MANAGEMENT") || "ניהול טוקן"}
            </h5>

            <Row className="mb-3">
              <Col md={4}>
                <Label className="text-muted">
                  {t("AGENT.TOKEN_ISSUED") || "תאריך יצירת טוקן"}
                </Label>
                <div className="fw-bold">{formatDateTime(tokenIssued)}</div>
              </Col>
              <Col md={4}>
                <Label className="text-muted">
                  {t("AGENT.TOKEN_EXPIRES") || "תאריך תפוגת טוקן"}
                </Label>
                <div className="fw-bold">
                  {formatDateTime(tokenExpires)}{" "}
                  {tokenExpires && (
                    isTokenExpired() ? (
                      <Badge color="danger">{t("AGENT.TOKEN_EXPIRED") || "פג תוקף"}</Badge>
                    ) : (
                      <Badge color="success">{t("AGENT.TOKEN_ACTIVE") || "פעיל"}</Badge>
                    )
                  )}
                </div>
              </Col>
              <Col md={4}>
                <Label className="text-muted">{t("AGENT.TOKEN_STATUS") || "סטטוס"}</Label>
                <div>
                  {tokenSuccess && (
                    <Badge color="success" className="p-2">
                      <i className="ri-check-double-line me-1"></i>
                      {t("AGENT.TOKEN_SUCCESS_BADGE") || "תגובה חיובית מכספונט"}
                    </Badge>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Button
                  color="success"
                  onClick={handleTokenAction}
                  disabled={isCreatingToken}
                  className="w-100"
                >
                  {isCreatingToken ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-add-circle-line me-1"></i>
                      {t("AGENT.CREATE_TOKEN") || "צור טוקן"}
                    </>
                  )}
                </Button>
              </Col>
              <Col md={4}>
                <Button
                  color="info"
                  onClick={handleTokenAction}
                  disabled={isCreatingToken}
                  className="w-100"
                >
                  {isCreatingToken ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-refresh-line me-1"></i>
                      {t("AGENT.REFRESH_TOKEN") || "רענן טוקן"}
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
    </div>
  );
}

export default CasponetConfig;
